import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Col,
  Form,
  Input,
  Layout as AntdLayout,
  Row,
  Typography,
  message,
} from "antd";
import { Logo } from "components/layout/logo";
import "../login/styles.css";

const { Title } = Typography;

type AcceptInviteForm = {
  password: string;
  confirmPassword: string;
};

export const AcceptInvitePage: React.FC = () => {
  const [form] = Form.useForm<AcceptInviteForm>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const token = useMemo(() => searchParams.get("token") ?? "", [searchParams]);

  const onFinish = async (values: AcceptInviteForm) => {
    if (!token) {
      message.error("Ogiltig inbjudningslänk");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/accept-invite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          password: values.password,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || data?.Message || "Kunde inte sätta lösenord");
      }

      message.success("Lösenordet är sparat. Du kan nu logga in.");
      navigate("/login");
    } catch (error: any) {
      message.error(error?.message || "Kunde inte sätta lösenord");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AntdLayout className="layout">
      <Row justify="center" align="middle" style={{ height: "100vh" }}>
        <Col span={0} lg={12} style={{ height: "100%" }}>
          <div className="image-container"></div>
        </Col>
        <Col span={24} lg={12} style={{ height: "100%" }}>
          <div className="white-container">
            <div className="container">
              <div className="imageContainer">
                <Logo collapsed />
              </div>
              <Title level={3} className="title">
                Acceptera inbjudan
              </Title>
              {!token ? (
                <Typography.Paragraph type="danger">
                  Inbjudningslänken saknar token.
                </Typography.Paragraph>
              ) : (
                <Form<AcceptInviteForm>
                  layout="vertical"
                  form={form}
                  onFinish={onFinish}
                  requiredMark={false}
                >
                  <Form.Item
                    name="password"
                    label="Nytt lösenord"
                    rules={[{ required: true, message: "Ange lösenord" }]}
                  >
                    <Input.Password size="large" />
                  </Form.Item>
                  <Form.Item
                    name="confirmPassword"
                    label="Bekräfta lösenord"
                    dependencies={["password"]}
                    rules={[
                      { required: true, message: "Bekräfta lösenordet" },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue("password") === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error("Lösenorden matchar inte"));
                        },
                      }),
                    ]}
                  >
                    <Input.Password size="large" />
                  </Form.Item>
                  <Form.Item>
                    <Button
                      type="primary"
                      size="large"
                      htmlType="submit"
                      loading={loading}
                      block
                    >
                      Spara lösenord
                    </Button>
                  </Form.Item>
                </Form>
              )}
            </div>
          </div>
        </Col>
      </Row>
    </AntdLayout>
  );
};
